import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import ProductSummary from 'components/coop/ProductSummary.js';

const Overlay = styled.div`${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`}`;
const Modal = styled.div`${tw`bg-white rounded-lg p-8 w-full max-w-4xl max-h-screen overflow-y-auto`}`;
const Title = tw.h2`text-xl font-bold mb-4 text-gray-800`;
const Label = tw.label`block text-sm font-semibold text-gray-600 mb-1`;
const Input = tw.input`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Select = tw.select`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Textarea = tw.textarea`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500 h-20`;
const Row = tw.div`flex gap-3 flex-wrap`;
const Col = styled.div`
  flex: 1;
  min-width: 200px;
`;
const ButtonRow = tw.div`flex justify-end gap-3 mt-4`;
const SaveBtn = tw.button`bg-green-500 text-white px-5 py-2 rounded font-semibold hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed`;
const RejectBtn = tw.button`bg-red-500 text-white px-5 py-2 rounded font-semibold hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed`;
const AddToExistingBtn = tw.button`bg-blue-500 text-white px-5 py-2 rounded font-semibold hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed`;
const CancelBtn = tw.button`bg-gray-200 text-gray-700 px-5 py-2 rounded font-semibold hover:bg-gray-300 transition`;

const TabContainer = tw.div`flex gap-2 mb-4 border-b border-gray-200`;
const Tab = styled.button`
  ${tw`px-4 py-2 text-sm font-semibold transition`}
  ${props => props.active ? tw`border-b-2 border-green-500 text-green-600` : tw`text-gray-500 hover:text-gray-700`}
`;

const CheckboxContainer = tw.div`flex items-center gap-2 mb-3 p-3 bg-blue-500 border border-blue-200 rounded`;
const Checkbox = tw.input`w-4 h-4 text-blue-600 border-gray-300 rounded`;
const CheckboxLabel = tw.label`text-sm text-gray-700 cursor-pointer select-none`;

export default function AdminInterestForm({ interest, existingGroups, onApproveAsNew, onAddToExisting, onReject, onCancel }) {
    const [activeTab, setActiveTab] = useState('new');
    const [saving, setSaving] = useState(false);
    
    // Form state for new group creation
    const [name, setName] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [bulkItem, setBulkItem] = useState('');
    const [targetAmount, setTargetAmount] = useState('');
    const [maxMembers, setMaxMembers] = useState('');
    const [dropoffAddress, setDropoffAddress] = useState('');
    const [expiryHours, setExpiryHours] = useState('72');
    
    // Auto-join settings
    const [autoJoinSubmitter, setAutoJoinSubmitter] = useState(true);
    const [submitterContribution, setSubmitterContribution] = useState('');
    const [submitterPortion, setSubmitterPortion] = useState('');
    
    // For adding to existing group
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [addAsFirstMember, setAddAsFirstMember] = useState(true);
    const [existingContribution, setExistingContribution] = useState('');
    const [existingPortion, setExistingPortion] = useState('');

    useEffect(() => {
        if (interest) {
            setName(interest.name || '');
            setNeighborhood(interest.neighborhood || '');
            setBulkItem(interest.bulkItem || '');
            setTargetAmount(interest.targetAmount || '');
            setMaxMembers(interest.maxMembers || '');
            setDropoffAddress(interest.dropoffAddress || '');
            
            // Pre-fill contribution if provided in interest
            setSubmitterContribution(interest.contributionAmount || '');
            setSubmitterPortion(interest.portionRequested || '');
            setExistingContribution(interest.contributionAmount || '');
            setExistingPortion(interest.portionRequested || '');
        }
    }, [interest]);

    const handleApproveAsNew = async (e) => {
        e.preventDefault();
        
        if (autoJoinSubmitter && (!submitterContribution || Number(submitterContribution) <= 0)) {
            alert('Please enter a valid contribution amount for the submitter');
            return;
        }
        
        setSaving(true);
        try {
            const expiryDate = new Date();
            expiryDate.setHours(expiryDate.getHours() + Number(expiryHours));
            
            const data = {
                name,
                neighborhood,
                bulkItem,
                targetAmount: Number(targetAmount),
                maxMembers: Number(maxMembers),
                dropoffAddress,
                expiryDate: expiryDate.toISOString(),
                autoJoinSubmitter,
                submitterContribution: autoJoinSubmitter ? Number(submitterContribution) : null,
                submitterPortion: autoJoinSubmitter ? submitterPortion : null,
                marketplaceProducts: interest.marketplaceProducts || []
            };
            await onApproveAsNew(data);
        } catch (err) {
            console.error('AdminInterestForm approve error:', err);
            alert('Approval failed: ' + (err?.message || JSON.stringify(err)));
        } finally {
            setSaving(false);
        }
    };

    const handleAddToExisting = async (e) => {
        e.preventDefault();
        if (!selectedGroupId) {
            alert('Please select a group');
            return;
        }
        
        if (addAsFirstMember && (!existingContribution || Number(existingContribution) <= 0)) {
            alert('Please enter a valid contribution amount');
            return;
        }
        
        setSaving(true);
        try {
            await onAddToExisting(selectedGroupId, {
                addAsMember: addAsFirstMember,
                contribution: addAsFirstMember ? Number(existingContribution) : null,
                portion: addAsFirstMember ? existingPortion : null
            });
        } catch (err) {
            console.error('AdminInterestForm add to existing error:', err);
            alert('Failed to add to group: ' + (err?.message || JSON.stringify(err)));
        } finally {
            setSaving(false);
        }
    };

    const handleReject = async () => {
        if (!window.confirm('Are you sure you want to reject this interest?')) return;
        setSaving(true);
        try {
            await onReject();
        } catch (err) {
            console.error('AdminInterestForm reject error:', err);
            alert('Rejection failed: ' + (err?.message || JSON.stringify(err)));
        } finally {
            setSaving(false);
        }
    };

    if (!interest) return null;

    // Filter groups by neighborhood
    const matchingGroups = existingGroups.filter(g => 
        g.neighborhood === interest.neighborhood && 
        g.status === 'active' &&
        g.bulkItem === interest.bulkItem
    );

    return (
        <Overlay onClick={onCancel}>
            <Modal onClick={e => e.stopPropagation()}>
                <Title>Review Co-Op Interest</Title>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                    Submitted by: <strong style={{ color: '#374151' }}>{interest.submittedByName}</strong> ({interest.submittedByEmail})
                    <br />
                    Submitted on: <strong>{new Date(interest.submittedAt).toLocaleString()}</strong>
                </div>

                {/* Display selected marketplace products if any */}
                {interest.marketplaceProducts && interest.marketplaceProducts.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                        <ProductSummary products={interest.marketplaceProducts} showRemove={false} />
                        <div style={{ marginTop: '0.5rem', padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                            <div style={{ fontSize: '0.75rem', color: '#92400e' }}>
                                <strong>📦 Marketplace Products Selected:</strong> The submitter has selected {interest.marketplaceProducts.length} product(s) from the marketplace. 
                                Total calculated: ₦{interest.marketplaceProducts.reduce((sum, p) => sum + (p.unitPrice * p.quantity), 0).toLocaleString()}
                            </div>
                        </div>
                    </div>
                )}

                <TabContainer>
                    <Tab active={activeTab === 'new'} onClick={() => setActiveTab('new')}>
                        Approve as New Group
                    </Tab>
                    <Tab active={activeTab === 'existing'} onClick={() => setActiveTab('existing')}>
                        Add to Existing Group ({matchingGroups.length})
                    </Tab>
                </TabContainer>

                {activeTab === 'new' ? (
                    <form onSubmit={handleApproveAsNew}>
                        <Label>Co-Op Group Name *</Label>
                        <Input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. Surulere Rice and Grains Pool"
                            required
                        />

                        <Row>
                            <Col>
                                <Label>Neighborhood Area *</Label>
                                <Select
                                    value={neighborhood}
                                    onChange={e => setNeighborhood(e.target.value)}
                                    required
                                >
                                    <option value="">Select...</option>
                                    <option value="Ikeja">Ikeja</option>
                                    <option value="Lekki">Lekki</option>
                                    <option value="Yaba">Yaba</option>
                                    <option value="Gbagada">Gbagada</option>
                                    <option value="Surulere">Surulere</option>
                                </Select>
                            </Col>
                            <Col>
                                <Label>Bulk Item Pool Category *</Label>
                                <Select
                                    value={bulkItem}
                                    onChange={e => setBulkItem(e.target.value)}
                                    required
                                >
                                    <option value="">Select...</option>
                                    <option value="Rice Sacks">Rice Sacks</option>
                                    <option value="Beans Sacks">Beans Sacks</option>
                                    <option value="Garri Basins">Garri Basins</option>
                                    <option value="Vegetable Oil Kegs">Vegetable Oil Kegs</option>
                                    <option value="Tomato Baskets">Tomato Baskets</option>
                                </Select>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Label>Total Cost Goal (₦) *</Label>
                                <Input
                                    type="number"
                                    value={targetAmount}
                                    onChange={e => setTargetAmount(e.target.value)}
                                    placeholder="e.g. 45000"
                                    required
                                />
                            </Col>
                            <Col>
                                <Label>Member Limit (Optional)</Label>
                                <Input
                                    type="number"
                                    value={maxMembers}
                                    onChange={e => setMaxMembers(e.target.value)}
                                    placeholder="Unlimited"
                                />
                            </Col>
                            <Col>
                                <Label>Expiry (Hours, Max 72) *</Label>
                                <Input
                                    type="number"
                                    value={expiryHours}
                                    onChange={e => setExpiryHours(Math.min(72, Math.max(1, e.target.value)))}
                                    placeholder="72"
                                    min="1"
                                    max="72"
                                    required
                                />
                            </Col>
                        </Row>

                        <Label>Neighborhood Central Drop-off Site Address *</Label>
                        <Textarea
                            value={dropoffAddress}
                            onChange={e => setDropoffAddress(e.target.value)}
                            placeholder="Specify a coordinator's house or accessible drop-off spot in this area"
                            required
                        />

                        {/* Auto-join submitter checkbox */}
                        <CheckboxContainer>
                            <Checkbox
                                type="checkbox"
                                id="autoJoinSubmitter"
                                checked={autoJoinSubmitter}
                                onChange={e => setAutoJoinSubmitter(e.target.checked)}
                            />
                            <CheckboxLabel htmlFor="autoJoinSubmitter">
                                <strong>Add submitter as first member and group admin</strong> (Recommended)
                            </CheckboxLabel>
                        </CheckboxContainer>

                        {/* Collapsible contribution fields */}
                        {autoJoinSubmitter && (
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', backgroundColor: '#f9fafb' }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
                                    Submitter's Initial Contribution
                                </div>
                                <Row>
                                    <Col>
                                        <Label>Contribution Amount (₦) *</Label>
                                        <Input
                                            type="number"
                                            value={submitterContribution}
                                            onChange={e => setSubmitterContribution(e.target.value)}
                                            placeholder="e.g. 5000"
                                            required={autoJoinSubmitter}
                                            style={{ marginBottom: 0 }}
                                        />
                                    </Col>
                                    <Col>
                                        <Label>Portion/Fraction *</Label>
                                        <Input
                                            type="text"
                                            value={submitterPortion}
                                            onChange={e => setSubmitterPortion(e.target.value)}
                                            placeholder="e.g. 1/5 share of Rice Sacks"
                                            required={autoJoinSubmitter}
                                            style={{ marginBottom: 0 }}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        )}

                        <ButtonRow>
                            <CancelBtn type="button" onClick={onCancel}>Cancel</CancelBtn>
                            <RejectBtn type="button" onClick={handleReject} disabled={saving}>Reject Interest</RejectBtn>
                            <SaveBtn type="submit" disabled={saving}>
                                {saving ? 'Creating...' : 'Approve & Create Group'}
                            </SaveBtn>
                        </ButtonRow>
                    </form>
                ) : (
                    <form onSubmit={handleAddToExisting}>
                        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem' }}>Interest Details:</div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                <div>Neighborhood: <strong>{interest.neighborhood}</strong></div>
                                <div>Bulk Item: <strong>{interest.bulkItem}</strong></div>
                                <div>Target Amount: <strong>₦{Number(interest.targetAmount).toLocaleString()}</strong></div>
                            </div>
                        </div>

                        <Label>Select Existing Co-Op Group *</Label>
                        {matchingGroups.length > 0 ? (
                            <>
                                <Select
                                    value={selectedGroupId}
                                    onChange={e => setSelectedGroupId(e.target.value)}
                                    required
                                >
                                    <option value="">-- Choose a group --</option>
                                    {matchingGroups.map(g => (
                                        <option key={g.firestoreId} value={g.firestoreId}>
                                            {g.name} - {g.neighborhood} ({g.members?.length || 0}/{g.maxMembers ? g.maxMembers : "Unlimited"} members)
                                        </option>
                                    ))}
                                </Select>

                                {/* Add as member checkbox */}
                                <CheckboxContainer>
                                    <Checkbox
                                        type="checkbox"
                                        id="addAsFirstMember"
                                        checked={addAsFirstMember}
                                        onChange={e => setAddAsFirstMember(e.target.checked)}
                                    />
                                    <CheckboxLabel htmlFor="addAsFirstMember">
                                        <strong>Add submitter as member of selected group</strong> (Recommended)
                                    </CheckboxLabel>
                                </CheckboxContainer>

                                {/* Collapsible contribution fields */}
                                {addAsFirstMember && (
                                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', backgroundColor: '#f9fafb' }}>
                                        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem', color: '#374151' }}>
                                            Submitter's Contribution to Selected Group
                                        </div>
                                        <Row>
                                            <Col>
                                                <Label>Contribution Amount (₦) *</Label>
                                                <Input
                                                    type="number"
                                                    value={existingContribution}
                                                    onChange={e => setExistingContribution(e.target.value)}
                                                    placeholder="e.g. 5000"
                                                    required={addAsFirstMember}
                                                    style={{ marginBottom: 0 }}
                                                />
                                            </Col>
                                            <Col>
                                                <Label>Portion/Fraction *</Label>
                                                <Input
                                                    type="text"
                                                    value={existingPortion}
                                                    onChange={e => setExistingPortion(e.target.value)}
                                                    placeholder="e.g. 1/5 share of Rice Sacks"
                                                    required={addAsFirstMember}
                                                    style={{ marginBottom: 0 }}
                                                />
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
                                No matching active groups found in {interest.neighborhood} for {interest.bulkItem}.
                                <br />
                                Please approve as a new group instead.
                            </div>
                        )}

                        <ButtonRow>
                            <CancelBtn type="button" onClick={onCancel}>Cancel</CancelBtn>
                            <RejectBtn type="button" onClick={handleReject} disabled={saving}>Reject Interest</RejectBtn>
                            {matchingGroups.length > 0 && (
                                <AddToExistingBtn type="submit" disabled={saving}>
                                    {saving ? 'Adding...' : 'Add to Selected Group'}
                                </AddToExistingBtn>
                            )}
                        </ButtonRow>
                    </form>
                )}
            </Modal>
        </Overlay>
    );
}
